(function () {
  const kqvCartFetchPatch = (() => {
    if (window.__kqvCartFetchPatchApi) {
      return window.__kqvCartFetchPatchApi;
    }

    const contextsByVariant = new Map();
    const cartTransformers = [];
    const originalFetch = window.fetch.bind(window);

    const getUrl = (input) => {
      if (typeof input === 'string') return input;
      return input?.url || '';
    };

    const isCartAddUrl = (url) =>
      String(url).includes('/cart/add');

    const getPropertiesFromFormData = (formData) => {
      const properties = {};

      for (const [key, value] of formData.entries()) {
        const match = key.match(
          /^properties\[(.+)]$/
        );

        if (!match || value === '') continue;

        properties[match[1]] = value;
      }

      return properties;
    };

    const createJsonHeaders = (headersInit) => {
      const headers = new Headers(
        headersInit || {}
      );

      headers.set(
        'Content-Type',
        'application/json'
      );

      headers.set(
        'Accept',
        'application/json'
      );

      headers.set(
        'X-Requested-With',
        'XMLHttpRequest'
      );

      return headers;
    };

    /*
     * Shopify retourne un objet avec items[]
     * lorsque l'ajout utilise le format JSON.
     *
     * Dawn attend cependant les données de la ligne
     * ajoutée à la racine de la réponse.
     *
     * On normalise donc la réponse avant qu'elle
     * ne soit lue par product-form.js.
     */
    const normalizeResponseForDawn = async (
      response,
      fallbackVariantId
    ) => {
      let data;

      try {
        data = await response.clone().json();
      } catch (error) {
        return response;
      }

      const firstItem =
        Array.isArray(data?.items) &&
        data.items.length
          ? data.items[0]
          : null;

      if (!firstItem) {
        return response;
      }

      const normalizedData = {
        ...firstItem,
        ...data,

        id:
          firstItem.id ||
          firstItem.variant_id ||
          fallbackVariantId,
      };

      const headers = new Headers(
        response.headers
      );

      headers.delete('content-length');
      headers.delete('content-encoding');
      headers.delete('transfer-encoding');

      return new Response(
        JSON.stringify(normalizedData),
        {
          status: response.status,
          statusText: response.statusText,
          headers,
        }
      );
    };

    const register = (
      variantId,
      context
    ) => {
      if (!variantId || !context) return;

      const key = String(variantId);

      const contexts =
        contextsByVariant.get(key) || [];

      contexts.push(context);

      contextsByVariant.set(
        key,
        contexts
      );
    };

    const registerCartTransformer = (
      transformer
    ) => {
      if (
        typeof transformer !== 'function' ||
        cartTransformers.includes(transformer)
      ) {
        return;
      }

      cartTransformers.push(transformer);
    };

    const findActiveContext = (
      variantId
    ) => {
      const contexts =
        contextsByVariant.get(
          String(variantId)
        ) || [];

      return (
        contexts.find(
          (context) =>
            context.isActive?.() === true &&
            context.getPurchaseType?.() ===
              'subscription' &&
            context.isSubscriptionAvailable?.() ===
              true
        ) || null
      );
    };

    const buildPayloadFromFormData = (
      formData
    ) => {
      const variantId = Number(
        formData.get('id')
      );

      if (!Number.isFinite(variantId)) {
        return null;
      }

      const rawQuantity = Number(
        formData.get('quantity') || 1
      );

      const quantity =
        Number.isFinite(rawQuantity) &&
        rawQuantity > 0
          ? rawQuantity
          : 1;

      const item = {
        id: variantId,
        quantity,
      };

      const properties =
        getPropertiesFromFormData(
          formData
        );

      if (
        Object.keys(properties).length
      ) {
        item.properties = properties;
      }

      const payload = {
        items: [item],
      };

      const sections =
        formData.get('sections');

      const sectionsUrl =
        formData.get('sections_url');

      if (sections) {
        payload.sections = sections;
      }

      if (sectionsUrl) {
        payload.sections_url =
          sectionsUrl;
      }

      return {
        payload,
        variantId,
      };
    };

    const buildSubscriptionPayloadFromFormData = (
      formData,
      context
    ) => {
      const base =
        buildPayloadFromFormData(
          formData
        );

      if (!base) return null;

      const sellingPlanId = Number(
        context.getSellingPlanId?.()
      );

      if (
        !Number.isFinite(sellingPlanId)
      ) {
        return null;
      }

      base.payload.items[0].selling_plan =
        sellingPlanId;

      return base;
    };

    const applyCartTransformers = (
      payload,
      variantId
    ) => {
      let currentPayload = payload;
      let currentVariantId = variantId;
      let changed = false;

      cartTransformers.forEach(
        (transformer) => {
          let result = null;

          try {
            result = transformer({
              payload: currentPayload,
              variantId:
                currentVariantId,
            });
          } catch (error) {
            console.error(
              '[KQV] cart transformer error',
              error
            );
          }

          if (!result) return;

          if (result.payload) {
            currentPayload =
              result.payload;
          }

          if (
            result.variantId !==
              undefined &&
            result.variantId !== null &&
            result.variantId !== ''
          ) {
            currentVariantId =
              result.variantId;
          }

          changed = true;
        }
      );

      return {
        payload: currentPayload,
        variantId:
          currentVariantId,
        changed,
      };
    };

    /*
     * Si une autre couche du thème envoie déjà
     * du JSON, on injecte selling_plan si besoin,
     * puis on laisse les composants enregistrés
     * compléter le payload (upsells, gifts, etc.).
     */
    const patchJsonPayload = (
      rawBody
    ) => {
      let payload;

      try {
        payload = JSON.parse(rawBody);
      } catch (error) {
        return null;
      }

      if (
        !Array.isArray(payload?.items) ||
        !payload.items.length
      ) {
        return null;
      }

      let changed = false;
      let variantId = Number(
        payload.items[0]?.id || 0
      );

      const matchedItem =
        payload.items.find((item) => {
          const context =
            findActiveContext(item?.id);

          return Boolean(context);
        });

      if (matchedItem) {
        const context =
          findActiveContext(
            matchedItem.id
          );

        const sellingPlanId = Number(
          context?.getSellingPlanId?.()
        );

        if (
          Number.isFinite(
            sellingPlanId
          )
        ) {
          matchedItem.selling_plan =
            sellingPlanId;

          variantId = Number(
            matchedItem.id
          );

          changed = true;
        }
      }

      const extra =
        applyCartTransformers(
          payload,
          variantId
        );

      if (extra.changed) {
        payload = extra.payload;
        variantId = extra.variantId;
        changed = true;
      }

      if (!changed) {
        return null;
      }

      return {
        payload,
        variantId,
      };
    };

    window.fetch = async function (
      input,
      init = {}
    ) {
      const url = getUrl(input);

      if (!isCartAddUrl(url)) {
        return originalFetch(
          input,
          init
        );
      }

      let transformed = null;

      if (
        typeof FormData !==
          'undefined' &&
        init.body instanceof FormData
      ) {
        const variantId = String(
          init.body.get('id') || ''
        );

        const context =
          findActiveContext(
            variantId
          );

        let base = null;

        if (context) {
          base =
            buildSubscriptionPayloadFromFormData(
              init.body,
              context
            );
        } else if (
          cartTransformers.length
        ) {
          base =
            buildPayloadFromFormData(
              init.body
            );
        }

        if (base) {
          const extra =
            applyCartTransformers(
              base.payload,
              base.variantId
            );

          if (
            context ||
            extra.changed
          ) {
            transformed = {
              payload:
                extra.payload,
              variantId:
                extra.variantId,
            };
          }
        }
      } else if (
        typeof init.body ===
          'string' &&
        init.body
          .trim()
          .startsWith('{')
      ) {
        transformed =
          patchJsonPayload(
            init.body
          );
      }

      /*
       * Requête qui ne vient pas du picker / upsell :
       * aucun changement.
       */
      if (!transformed) {
        return originalFetch(
          input,
          init
        );
      }

      const response =
        await originalFetch(input, {
          ...init,

          headers:
            createJsonHeaders(
              init.headers
            ),

          body: JSON.stringify(
            transformed.payload
          ),
        });

      return normalizeResponseForDawn(
        response,
        transformed.variantId
      );
    };

    const api = {
      register,
      registerCartTransformer,
    };

    window.__kqvCartFetchPatchApi =
      api;

    return api;
  })();

  function initKaijuQuantityVariantPickers(
    root = document
  ) {
    const pickers = Array.from(
      root.querySelectorAll('.kqv')
    );

    if (!pickers.length) return;

    pickers.forEach((picker) => {
      if (
        picker.dataset
          .kqvInitialized === 'true'
      ) {
        return;
      }

      const cards = Array.from(
        picker.querySelectorAll(
          '[data-kqv-card]'
        )
      );

      if (!cards.length) return;

      picker.dataset.kqvInitialized =
        'true';

      const formId =
        picker.dataset.productFormId;

      const isSizeQuantityMode =
        picker.dataset.kqvMode ===
        'size-quantity';

      const hasSubscription =
        picker.dataset
          .kqvHasSubscription ===
        'true';

      const sellingPlanId = String(
        picker.dataset
          .sellingPlanId || ''
      ).trim();

      const hasValidSellingPlanId =
        /^\d+$/.test(
          sellingPlanId
        );

      const renewalText =
        picker.dataset
          .subscriptionRenewalText ||
        'Renews every 28 days';

      const purchaseOptions =
        Array.from(
          picker.querySelectorAll(
            '[data-kqv-purchase-option]'
          )
        );

      let activePurchaseType =
        'one-time';

      /* =====================================================
         FORM
         ===================================================== */

      const resolveFormElement = (
        element
      ) => {
        if (!element) return null;

        if (
          element.tagName === 'FORM'
        ) {
          return element;
        }

        return (
          element.querySelector?.(
            'form'
          ) || null
        );
      };

      const getForm = () => {
        if (formId) {
          const exactForm =
            resolveFormElement(
              document.getElementById(
                formId
              )
            );

          if (exactForm) {
            return exactForm;
          }
        }

        return (
          picker.closest('form') ||
          picker
            .closest('product-form')
            ?.querySelector('form') ||
          null
        );
      };

      const getFormElements = () => {
        const form = getForm();

        return form?.elements
          ? Array.from(form.elements)
          : [];
      };

      const getVariantInputs = () => {
        return getFormElements().filter(
          (element) =>
            element.name === 'id' ||
            element.classList?.contains(
              'product-variant-id'
            )
        );
      };

      const getSubmitButton = () => {
        const form = getForm();

        return (
          getFormElements().find(
            (element) =>
              element.name === 'add'
          ) ||
          form?.querySelector(
            '[name="add"]'
          ) ||
          null
        );
      };

      const getAtcLabel = () => {
        return (
          getForm()?.querySelector(
            '.atc-label'
          ) || null
        );
      };

      const getAtcDiscount = () => {
        return (
          getForm()?.querySelector(
            '.qty-test-atc__discount'
          ) || null
        );
      };

      const getDynamicCheckout = () => {
        return (
          getForm()?.querySelector(
            '.shopify-payment-button'
          ) || null
        );
      };

      const getSelectedCard = () => {
        return (
          cards.find((card) =>
            card.classList.contains(
              'is-selected'
            )
          ) || null
        );
      };

      const addToCartLabel =
        picker.dataset
          .labelAddToCart ||
        'Add to cart';

      const soldOutLabel =
        picker.dataset.labelSoldOut ||
        'Sold out';

      /* =====================================================
         PACKS / DISCOUNTS
         ===================================================== */

      const getPackCount = (
        card
      ) => {
        if (!card) return 1;

        const rawValue =
          card.dataset.packKey ||
          card.querySelector(
            '.kqv__pack-title'
          )?.textContent ||
          '';

        const match = String(
          rawValue
        ).match(
          /\d+(?:[.,]\d+)?/
        );

        if (!match) return 1;

        const count = Number(
          match[0].replace(',', '.')
        );

        return Number.isFinite(
          count
        )
          ? count
          : 1;
      };

      const getSinglePackCard = () => {
        if (isSizeQuantityMode) {
          return null;
        }

        return (
          cards.find(
            (card) =>
              getPackCount(card) === 1
          ) || null
        );
      };

      /*
       * Legacy helpers kept for Gel / compatibility.
       * Main ATC badges now use the real price difference
       * calculated inside getPricingForCard().
       */
      const getPackDiscount = (
        card
      ) => {
        const packCount =
          getPackCount(card);

        if (!isSizeQuantityMode) {
          if (packCount === 3) {
            return 15;
          }

          if (packCount === 6) {
            return 25;
          }
        }

        const liquidDiscount =
          Number(
            card?.dataset.discount ||
              0
          );

        return liquidDiscount > 0
          ? liquidDiscount
          : 0;
      };

      const getSubscriptionDiscount = (
        card
      ) => {
        const liquidDiscount =
          Number(
            card?.dataset
              .subscriptionDiscount ||
              0
          );

        if (liquidDiscount > 0) {
          return liquidDiscount;
        }

        const oneTimePrice = Number(
          card?.dataset.price || 0
        );

        const subscriptionPrice =
          Number(
            card?.dataset
              .subscriptionPrice || 0
          );

        if (
          oneTimePrice > 0 &&
          subscriptionPrice > 0 &&
          subscriptionPrice <
            oneTimePrice
        ) {
          return (
            ((oneTimePrice -
              subscriptionPrice) /
              oneTimePrice) *
            100
          );
        }

        return 0;
      };

      const getTotalSubscriptionDiscount = (
        card
      ) => {
        const packDiscount =
          getPackDiscount(card);

        const subscriptionDiscount =
          getSubscriptionDiscount(card);

        return (
          100 -
          ((100 - packDiscount) *
            (100 -
              subscriptionDiscount)) /
            100
        );
      };

      const roundPercent = (
        value
      ) => {
        return (
          Math.round(
            (Number(value) +
              Number.EPSILON) *
              10
          ) / 10
        );
      };

      const formatPercent = (
        value
      ) => {
        const rounded =
          roundPercent(value);

        try {
          return new Intl.NumberFormat(
            document.documentElement
              .lang || undefined,
            {
              minimumFractionDigits: 0,
              maximumFractionDigits: 1,
            }
          ).format(rounded);
        } catch (error) {
          return Number.isInteger(
            rounded
          )
            ? String(rounded)
            : rounded.toFixed(1);
        }
      };

      /* =====================================================
         MONEY / COMPARE PRICE
         ===================================================== */

      const formatMoneyLike = (
        cents,
        sampleFormatted
      ) => {
        const amount = Number(cents);

        if (
          !Number.isFinite(amount)
        ) {
          return '';
        }

        const sample = String(
          sampleFormatted || ''
        );

        const decimalSeparator =
          sample.includes(',')
            ? ','
            : '.';

        const hasDecimals =
          Math.abs(amount % 100) > 0;

        let number = (
          amount / 100
        ).toFixed(
          hasDecimals ? 2 : 0
        );

        if (
          decimalSeparator === ','
        ) {
          number = number.replace(
            '.',
            ','
          );
        }

        const symbol = sample
          .replace(
            /[0-9.,\s\u00a0-]/g,
            ''
          )
          .trim();

        if (!symbol) {
          return number;
        }

        const firstDigitIndex =
          sample.search(/\d/);

        const firstSymbolIndex =
          sample.search(
            /[^\d.,\s\u00a0-]/
          );

        const symbolBefore =
          firstSymbolIndex !== -1 &&
          firstDigitIndex !== -1 &&
          firstSymbolIndex <
            firstDigitIndex;

        return symbolBefore
          ? `${symbol}${number}`
          : `${number}${symbol}`;
      };

      const getReferencePrice = (
        card
      ) => {
        if (!card) {
          return {
            cents: 0,
            formatted: '',
          };
        }

        const currentPrice = Number(
          card.dataset.price || 0
        );

        const currentPriceFormatted =
          card.dataset
            .priceFormatted || '';

        /*
         * Quantity products:
         * reference = 1-box price × quantity.
         */
        if (!isSizeQuantityMode) {
          const packCount =
            getPackCount(card);

          const singlePackPrice =
            Number(
              getSinglePackCard()
                ?.dataset.price || 0
            );

          if (
            packCount > 1 &&
            singlePackPrice > 0
          ) {
            const referencePrice =
              singlePackPrice *
              packCount;

            return {
              cents: referencePrice,

              formatted:
                formatMoneyLike(
                  referencePrice,
                  currentPriceFormatted
                ),
            };
          }
        }

        const compareAtPrice =
          Number(
            card.dataset
              .compareAtPrice || 0
          );

        if (
          compareAtPrice >
          currentPrice
        ) {
          return {
            cents: compareAtPrice,

            formatted:
              card.dataset
                .compareAtPriceFormatted ||
              formatMoneyLike(
                compareAtPrice,
                currentPriceFormatted
              ),
          };
        }

        return {
          cents: 0,
          formatted: '',
        };
      };

      const isSubscriptionAvailableForCard = (
        card
      ) => {
        if (
          !hasSubscription ||
          !hasValidSellingPlanId ||
          !card
        ) {
          return false;
        }

        return (
          card.dataset.available ===
            'true' &&
          card.dataset
            .subscriptionAvailable ===
            'true'
        );
      };

      /*
       * IMPORTANT:
       *
       * Badge is now calculated from the REAL price:
       *
       * reference = 1-box price × pack count
       * discount  = (reference - actual price) / reference
       *
       * This means Hydrate, Kick A and Kick B can all
       * use the same code without hardcoded 15% / 25%.
       */
      const getPricingForCard = (
        card
      ) => {
        const reference =
          getReferencePrice(card);

        const useSubscription =
          activePurchaseType ===
            'subscription' &&
          isSubscriptionAvailableForCard(
            card
          );

        const actualPrice =
          useSubscription
            ? Number(
                card.dataset
                  .subscriptionPrice ||
                card.dataset.price ||
                0
              )
            : Number(
                card.dataset.price ||
                0
              );

        const priceFormatted =
          useSubscription
            ? (
                card.dataset
                  .subscriptionPriceFormatted ||
                card.dataset
                  .priceFormatted ||
                ''
              )
            : (
                card.dataset
                  .priceFormatted ||
                ''
              );

        const discount =
          reference.cents > actualPrice &&
          reference.cents > 0 &&
          actualPrice > 0
            ? Math.round(
                (
                  (
                    reference.cents -
                    actualPrice
                  ) /
                  reference.cents
                ) *
                  100
              )
            : 0;

        return {
          priceFormatted,

          referencePrice:
            reference.cents,

          referencePriceFormatted:
            reference.formatted,

          discount,

          discountFormatted:
            String(discount),
        };
      };

      /* =====================================================
         SELLING PLAN INPUT
         ===================================================== */

      const getSellingPlanInputs = (
        form
      ) => {
        if (!form?.elements) {
          return [];
        }

        return Array.from(
          form.elements
        ).filter(
          (element) =>
            element.name ===
            'selling_plan'
        );
      };

      const ensureSellingPlanInput = (
        form
      ) => {
        let input =
          getSellingPlanInputs(
            form
          ).find(
            (element) =>
              element.dataset
                .kqvSellingPlan ===
              'true'
          );

        if (!input) {
          input =
            document.createElement(
              'input'
            );

          input.type = 'hidden';

          input.name =
            'selling_plan';

          input.dataset
            .kqvSellingPlan =
            'true';

          form.appendChild(input);
        }

        return input;
      };

      const syncSellingPlanInput = (
        card
      ) => {
        const form = getForm();

        if (!form || !card) return;

        const customInput =
          ensureSellingPlanInput(
            form
          );

        /*
         * Désactive les champs Appstle
         * concurrents.
         */
        getSellingPlanInputs(form)
          .filter(
            (input) =>
              input !== customInput
          )
          .forEach((input) => {
            input.disabled = true;

            if (
              input.type ===
                'radio' ||
              input.type ===
                'checkbox'
            ) {
              input.checked = false;
            }
          });

        const shouldSubscribe =
          activePurchaseType ===
            'subscription' &&
          isSubscriptionAvailableForCard(
            card
          );

        customInput.value =
          shouldSubscribe
            ? sellingPlanId
            : '';

        customInput.disabled =
          !shouldSubscribe;

        form.dataset
          .kqvPurchaseType =
          activePurchaseType;

        form.dataset
          .kqvSellingPlanId =
          shouldSubscribe
            ? sellingPlanId
            : '';
      };

      /*
       * Chaque variante enregistre son contexte.
       * Le patch fetch vérifiera ensuite quelle
       * variante est réellement sélectionnée.
       */
      cards.forEach((card) => {
        const variantId =
          card.dataset.variantId;

        if (!variantId) return;

        kqvCartFetchPatch.register(
          variantId,
          {
            getPurchaseType: () =>
              activePurchaseType,

            getSellingPlanId: () =>
              sellingPlanId,

            isActive: () => {
              const selectedCard =
                getSelectedCard();

              return (
                picker.isConnected &&
                selectedCard?.dataset
                  .variantId ===
                  variantId
              );
            },

            isSubscriptionAvailable:
              () => {
                const selectedCard =
                  getSelectedCard();

                return (
                  selectedCard?.dataset
                    .variantId ===
                    variantId &&
                  isSubscriptionAvailableForCard(
                    selectedCard
                  )
                );
              },
          }
        );
      });

      /* =====================================================
         UI
         ===================================================== */

      const updatePerServingPrices =
        () => {
          cards.forEach((card) => {
            const priceElement =
              card.querySelector(
                '[data-kqv-per-serving-price]'
              );

            if (!priceElement) {
              return;
            }

            const useSubscriptionPrice =
              activePurchaseType ===
                'subscription' &&
              isSubscriptionAvailableForCard(
                card
              );

            priceElement.textContent =
              useSubscriptionPrice
                ? card.dataset
                    .subscriptionPerServingPriceFormatted ||
                  card.dataset
                    .perServingPriceFormatted ||
                  ''
                : card.dataset
                    .perServingPriceFormatted ||
                  '';
          });
        };

      const updatePurchaseOptions = (
        card
      ) => {
        if (
          !hasSubscription ||
          !purchaseOptions.length ||
          !card
        ) {
          return;
        }

        const subscriptionAvailable =
          isSubscriptionAvailableForCard(
            card
          );

        /*
         * Si on revient sur une variante sans
         * abonnement disponible, on force
         * automatiquement le one-time.
         */
        if (
          activePurchaseType ===
            'subscription' &&
          !subscriptionAvailable
        ) {
          activePurchaseType =
            'one-time';
        }

        picker.dataset
          .kqvPurchaseType =
          activePurchaseType;

        purchaseOptions.forEach(
          (option) => {
            const purchaseType =
              option.dataset
                .purchaseType;

            const isSelected =
              purchaseType ===
              activePurchaseType;

            const isSubscriptionOption =
              purchaseType ===
              'subscription';

            const isDisabled =
              isSubscriptionOption &&
              !subscriptionAvailable;

            /*
             * 1-pack : l'option subscription
             * est complètement masquée.
             *
             * 3-pack / 6-pack : elle réapparaît
             * automatiquement.
             */
            if (
              isSubscriptionOption
            ) {
              option.hidden =
                !subscriptionAvailable;

              option.style.display =
                subscriptionAvailable
                  ? ''
                  : 'none';

              if (
                subscriptionAvailable
              ) {
                option.removeAttribute(
                  'aria-hidden'
                );

                option.removeAttribute(
                  'tabindex'
                );
              } else {
                option.setAttribute(
                  'aria-hidden',
                  'true'
                );

                option.setAttribute(
                  'tabindex',
                  '-1'
                );
              }
            }

            option.classList.toggle(
              'is-selected',
              isSelected
            );

            option.classList.toggle(
              'is-disabled',
              isDisabled
            );

            option.setAttribute(
              'aria-checked',
              isSelected
                ? 'true'
                : 'false'
            );

            option.setAttribute(
              'aria-disabled',
              isDisabled
                ? 'true'
                : 'false'
            );

            option.disabled =
              isDisabled;
          }
        );

        const oneTimePrice =
          picker.querySelector(
            '[data-kqv-one-time-price]'
          );

        const subscriptionPrice =
          picker.querySelector(
            '[data-kqv-subscription-price]'
          );

        const subscriptionCompare =
          picker.querySelector(
            '[data-kqv-subscription-compare]'
          );

        const subscriptionMeta =
          picker.querySelector(
            '[data-kqv-subscription-meta]'
          );

        const subscriptionDiscount =
          getSubscriptionDiscount(
            card
          );

        if (oneTimePrice) {
          oneTimePrice.textContent =
            card.dataset
              .priceFormatted || '';
        }

        if (subscriptionPrice) {
          subscriptionPrice.textContent =
            card.dataset
              .subscriptionPriceFormatted ||
            card.dataset
              .priceFormatted ||
            '';
        }

        if (subscriptionCompare) {
          subscriptionCompare.textContent =
            card.dataset
              .priceFormatted || '';

          subscriptionCompare.hidden =
            !subscriptionAvailable;
        }

        /*
         * Le bloc subscription affiche
         * le discount + la bottle offerte.
         */
        if (subscriptionMeta) {
          subscriptionMeta.textContent =
            subscriptionAvailable &&
            subscriptionDiscount > 0
              ? `-${formatPercent(
                  subscriptionDiscount
                )}% on every order + Free Kaiju bottle`
              : 'Free Kaiju bottle';
        }

        syncSellingPlanInput(card);
      };

      const updateAtc = (
        card
      ) => {
        if (!card) return;

        const pricing =
          getPricingForCard(card);

        const isAvailable =
          card.dataset.available ===
          'true';

        /*
         * Le bloc restock existe uniquement
         * sur le picker Gel.
         */
        const restockBlock =
          picker.querySelector(
            '[data-kqv-restock]'
          );

        const purchaseBlock =
          picker.querySelector(
            '[data-kqv-purchase-block]'
          );

        const showRestock =
          Boolean(restockBlock) &&
          !isAvailable;

        if (restockBlock) {
          restockBlock.hidden =
            !showRestock;

          restockBlock.setAttribute(
            'aria-hidden',
            showRestock
              ? 'false'
              : 'true'
          );
        }

        /*
         * Sur le Gel OOS, on masque tout le
         * bloc One Time / Subscription.
         */
        if (
          purchaseBlock &&
          restockBlock
        ) {
          purchaseBlock.hidden =
            showRestock;

          purchaseBlock.setAttribute(
            'aria-hidden',
            showRestock
              ? 'true'
              : 'false'
          );
        }

        const submitButton =
          getSubmitButton();

        if (submitButton) {
          /*
           * Pour le Gel OOS, on masque
           * complètement l'ATC.
           *
           * Sur les autres produits, le
           * comportement reste inchangé.
           */
          if (restockBlock) {
            submitButton.hidden =
              showRestock;
          }

          submitButton.disabled =
            !isAvailable;

          submitButton.setAttribute(
            'aria-disabled',
            isAvailable
              ? 'false'
              : 'true'
          );
        }

        /*
         * Sécurité supplémentaire si le thème
         * affiche un bouton de paiement dynamique.
         */
        const dynamicCheckout =
          getDynamicCheckout();

        if (
          dynamicCheckout &&
          restockBlock
        ) {
          dynamicCheckout.hidden =
            showRestock;
        }

        const atcLabel =
          getAtcLabel();

        if (atcLabel) {
          if (isAvailable) {
            const showCompare =
              pricing.discount > 0 &&
              pricing.referencePrice >
                0 &&
              pricing
                .referencePriceFormatted;

            atcLabel.innerHTML = `
              <span class="qty-test-atc__main">
                ${addToCartLabel} · <span class="qty-test-atc__price">${pricing.priceFormatted}</span>
              </span>

              <span class="qty-test-atc__compare"${
                showCompare
                  ? ''
                  : ' hidden'
              }>
                ${
                  showCompare
                    ? pricing.referencePriceFormatted
                    : ''
                }
              </span>
            `;
          } else {
            atcLabel.textContent =
              soldOutLabel;
          }
        }

        const atcDiscount =
          getAtcDiscount();

        if (atcDiscount) {
          if (
            isAvailable &&
            pricing.discount > 0
          ) {
            atcDiscount.textContent =
              `-${pricing.discountFormatted}%`;

            atcDiscount.hidden =
              false;
          } else {
            atcDiscount.textContent =
              '';

            atcDiscount.hidden =
              true;
          }
        }
      };

      const updateUI = (
        card,
        options = {}
      ) => {
        const allowDisabled =
          options.allowDisabled ||
          false;

        const syncVariant =
          options.syncVariant !==
          false;

        if (!card) return;

        const currentRadio =
          card.querySelector(
            'input[type="radio"]'
          );

        if (!allowDisabled) {
          if (card.hidden) return;

          if (
            currentRadio?.disabled
          ) {
            return;
          }

          if (
            card.dataset.available !==
            'true'
          ) {
            return;
          }
        }

        cards.forEach((item) => {
          item.classList.remove(
            'is-selected'
          );

          const radio =
            item.querySelector(
              'input[type="radio"]'
            );

          if (radio) {
            radio.checked = false;
          }
        });

        card.classList.add(
          'is-selected'
        );

        if (currentRadio) {
          currentRadio.checked =
            true;
        }

        if (
          activePurchaseType ===
            'subscription' &&
          !isSubscriptionAvailableForCard(
            card
          )
        ) {
          activePurchaseType =
            'one-time';
        }

        if (syncVariant) {
          const variantId =
            card.dataset.variantId;

          getVariantInputs().forEach(
            (input) => {
              input.value =
                variantId;

              input.disabled = false;

              input.dispatchEvent(
                new Event('change', {
                  bubbles: true,
                })
              );
            }
          );
        }

        updatePerServingPrices();
        updatePurchaseOptions(card);
        updateAtc(card);
      };

      /* =====================================================
         PURCHASE OPTIONS
         ===================================================== */

      const initPurchaseOptions =
        () => {
          if (
            !hasSubscription ||
            !purchaseOptions.length
          ) {
            return;
          }

          purchaseOptions.forEach(
            (option) => {
              option.addEventListener(
                'click',
                (event) => {
                  event.preventDefault();

                  const selectedCard =
                    getSelectedCard();

                  if (!selectedCard) {
                    return;
                  }

                  const purchaseType =
                    option.dataset
                      .purchaseType;

                  if (
                    purchaseType ===
                      'subscription' &&
                    !isSubscriptionAvailableForCard(
                      selectedCard
                    )
                  ) {
                    return;
                  }

                  activePurchaseType =
                    purchaseType ===
                    'subscription'
                      ? 'subscription'
                      : 'one-time';

                  updateUI(
                    selectedCard,
                    {
                      allowDisabled:
                        true,

                      syncVariant:
                        false,
                    }
                  );
                }
              );
            }
          );
        };

      /* =====================================================
         SIMPLE QUANTITY
         ===================================================== */

      const initSimpleQuantityPicker =
        () => {
          cards.forEach((card) => {
            card.addEventListener(
              'click',
              (event) => {
                event.preventDefault();

                const radio =
                  card.querySelector(
                    'input[type="radio"]'
                  );

                if (
                  radio?.disabled ||
                  card.dataset
                    .available !==
                    'true'
                ) {
                  return;
                }

                updateUI(card);
              }
            );

            const radio =
              card.querySelector(
                'input[type="radio"]'
              );

            radio?.addEventListener(
              'change',
              () => {
                if (
                  !radio.checked ||
                  radio.disabled ||
                  card.dataset
                    .available !==
                    'true'
                ) {
                  return;
                }

                updateUI(card);
              }
            );
          });

          const selectedCard =
            picker.querySelector(
              '.kqv__card.is-selected:not([hidden])'
            ) ||
            cards.find(
              (card) =>
                card.dataset
                  .available ===
                'true'
            ) ||
            cards[0];

          if (selectedCard) {
            updateUI(selectedCard, {
              allowDisabled:
                selectedCard.dataset
                  .available !==
                'true',
            });
          }
        };

      /* =====================================================
         SIZE + QUANTITY
         ===================================================== */

      const initSizeQuantityPicker =
        () => {
          const sizeCards =
            Array.from(
              picker.querySelectorAll(
                '[data-kqv-size-card]'
              )
            );

          const sizeRadios =
            Array.from(
              picker.querySelectorAll(
                '[data-kqv-size-radio]'
              )
            );

          const sizeBadge =
            picker.querySelector(
              '[data-kqv-size-badge]'
            );

          if (
            !sizeCards.length ||
            !sizeRadios.length
          ) {
            initSimpleQuantityPicker();
            return;
          }

          const getSelectedQuantityCard =
            () => {
              return (
                cards.find((card) =>
                  card.classList.contains(
                    'is-selected'
                  )
                ) || null
              );
            };

          const setSize = (
            sizeValue,
            preferredPackKey = '12'
          ) => {
            sizeCards.forEach(
              (sizeCard) => {
                const isSelected =
                  sizeCard.dataset
                    .sizeValue ===
                  sizeValue;

                const radio =
                  sizeCard.querySelector(
                    '[data-kqv-size-radio]'
                  );

                sizeCard.classList.toggle(
                  'is-selected',
                  isSelected
                );

                if (radio) {
                  radio.checked =
                    isSelected;
                }
              }
            );

            const selectedSizeCard =
              sizeCards.find(
                (sizeCard) =>
                  sizeCard.dataset
                    .sizeValue ===
                  sizeValue
              );

            if (
              sizeBadge &&
              selectedSizeCard
            ) {
              sizeBadge.textContent =
                `${selectedSizeCard.dataset.sizeLabel} per gel`;
            }

            const visibleCards =
              cards.filter(
                (card) =>
                  card.dataset
                    .sizeValue ===
                  sizeValue
              );

            cards.forEach((card) => {
              const shouldShow =
                card.dataset
                  .sizeValue ===
                sizeValue;

              const radio =
                card.querySelector(
                  'input[type="radio"]'
                );

              card.hidden =
                !shouldShow;

              if (radio) {
                radio.disabled =
                  !shouldShow ||
                  card.dataset
                    .available !==
                    'true';

                radio.checked =
                  false;
              }

              if (!shouldShow) {
                card.classList.remove(
                  'is-selected'
                );
              }
            });

            const nextCard =
              visibleCards.find(
                (card) =>
                  card.dataset
                    .packKey ===
                    preferredPackKey &&
                  card.dataset
                    .available ===
                    'true'
              ) ||
              visibleCards.find(
                (card) =>
                  card.dataset
                    .packKey ===
                    '12' &&
                  card.dataset
                    .available ===
                    'true'
              ) ||
              visibleCards.find(
                (card) =>
                  card.dataset
                    .available ===
                    'true'
              ) ||
              visibleCards[0];

            if (nextCard) {
              updateUI(nextCard, {
                allowDisabled:
                  nextCard.dataset
                    .available !==
                    'true',
              });
            }
          };

          sizeCards.forEach(
            (sizeCard) => {
              sizeCard.addEventListener(
                'click',
                (event) => {
                  event.preventDefault();

                  const radio =
                    sizeCard.querySelector(
                      '[data-kqv-size-radio]'
                    );

                  if (!radio) return;

                  const currentCard =
                    getSelectedQuantityCard();

                  setSize(
                    radio.value,
                    currentCard?.dataset
                      .packKey || '12'
                  );
                }
              );
            }
          );

          sizeRadios.forEach(
            (radio) => {
              radio.addEventListener(
                'change',
                () => {
                  if (
                    !radio.checked
                  ) {
                    return;
                  }

                  const currentCard =
                    getSelectedQuantityCard();

                  setSize(
                    radio.value,
                    currentCard?.dataset
                      .packKey || '12'
                  );
                }
              );
            }
          );

          cards.forEach((card) => {
            card.addEventListener(
              'click',
              (event) => {
                event.preventDefault();

                const radio =
                  card.querySelector(
                    'input[type="radio"]'
                  );

                if (
                  card.hidden ||
                  radio?.disabled ||
                  card.dataset
                    .available !==
                    'true'
                ) {
                  return;
                }

                updateUI(card);
              }
            );
          });

          const checkedSizeRadio =
            sizeRadios.find(
              (radio) =>
                radio.checked
            ) || sizeRadios[0];

          const selectedCard =
            getSelectedQuantityCard();

          if (checkedSizeRadio) {
            setSize(
              checkedSizeRadio.value,
              selectedCard?.dataset
                .packKey || '12'
            );
          }
        };

      initPurchaseOptions();

      if (isSizeQuantityMode) {
        initSizeQuantityPicker();
      } else {
        initSimpleQuantityPicker();
      }
    });
  }

  function bootKaijuQuantityVariantPickers() {
    initKaijuQuantityVariantPickers(
      document
    );
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      bootKaijuQuantityVariantPickers
    );
  } else {
    bootKaijuQuantityVariantPickers();
  }

  window.addEventListener(
    'load',
    bootKaijuQuantityVariantPickers
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initKaijuQuantityVariantPickers(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:select',
    (event) => {
      initKaijuQuantityVariantPickers(
        event.target
      );
    }
  );

  setTimeout(
    bootKaijuQuantityVariantPickers,
    300
  );

  setTimeout(
    bootKaijuQuantityVariantPickers,
    1000
  );
})();